import { useManager } from 'dreamstate';
import { ChangeEvent, useCallback, useRef, useState, FormEvent } from 'react';

import {
  rem,
  IconButton,
  InputGroup,
  InputLeftElement,
  Input,
  InputRightElement,
  Box,
} from '@vimeo/bokeh';
import { Send } from '@vimeo/bokeh-icons';
import { IBaseDomElementProps } from '@vimeo/core/types/dom';

import { ISelectedEmoji } from '../../../components/emoji';
import { browserConfig } from '../../../configs/BrowserConfig';
import { interactionToolsConfig } from '../../../configs/InteractionToolsConfig';
import { ChatManager, IChatContext } from '../../../store/firebase/interaction/chat';
import {
  trackSendBackstageMessage,
  trackSendMessage,
} from '../../../tracking/tracking_engagement_chat';
import { T_CHAT_DISABLED_NO_DOT, T_CHAT_PLACEHOLDER } from '../../../translations';
import { EChatType } from '../../../types/interaction_tools';
import { stopEventPropagation } from '../../../utils/dom/event';
import { createDomName } from '../../../utils/dom/identifying';
import { EMOJI_FONT_FAMILY } from '../../../utils/styling/layouts';
import { EmojiButton } from '../shared/EmojiButton';

interface IChatInputProps extends IBaseDomElementProps {
  chatType: EChatType;
  isDisabled?: boolean;
  placeholderText?: string;
  chatContext?: IChatContext;
}

export function ChatInput({
  id = createDomName('chat-input'),
  className = createDomName('chat-input'),
  chatType,
  isDisabled = false,
  placeholderText,
  chatContext: {
    isEnabled,
    chatActions: { sendMessage },
    // eslint-disable-next-line react-hooks/rules-of-hooks
  } = useManager(ChatManager),
}: IChatInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [message, setMessage] = useState<string>('');
  const [submitTimeout, setSubmitTimeout] = useState<boolean>(false);

  const onMessageEdited = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setMessage(event.target.value);
  }, []);

  const onSubmitMessage = useCallback(
    (event: FormEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();

      inputRef.current?.focus({ preventScroll: true });

      if (!message.length || message.length > interactionToolsConfig.CHAT.MAX_MESSAGE_LENGTH) {
        return;
      }

      if (submitTimeout) {
        return;
      } else {
        setSubmitTimeout(true);
      }

      setTimeout(
        () => setSubmitTimeout(false),
        interactionToolsConfig.CHAT.MESSAGE_SENDING_THROTTLE,
      );

      setMessage('');

      sendMessage(chatType, message);
      chatType === EChatType.PUBLIC ? trackSendMessage() : trackSendBackstageMessage();
    },
    [chatType, message, submitTimeout, sendMessage],
  );

  const onEmojiSelect = useCallback(
    (selection: ISelectedEmoji) => {
      if (inputRef.current) {
        const cursorPosition = inputRef.current.selectionStart || 0;
        const value = inputRef.current.value;
        // @ts-expect-error native is a boolean and does not have length property
        const newCaretPosition = cursorPosition + selection.native.length;
        const newValue =
          value.slice(0, cursorPosition) +
          selection.native +
          value.slice(cursorPosition, value.length);

        setMessage(newValue);

        inputRef.current.focus();
        inputRef.current.setSelectionRange(newCaretPosition, newCaretPosition);
      }
    },
    [setMessage],
  );

  const isChatEnabled: boolean = chatType === EChatType.PUBLIC ? Boolean(isEnabled) : true;
  const canInput: boolean = !isDisabled && isChatEnabled;
  const canSubmit: boolean = Boolean(canInput && message.length && !submitTimeout);

  const placeholder: string =
    placeholderText ||
    (isChatEnabled ? T_CHAT_PLACEHOLDER[chatType].input : T_CHAT_DISABLED_NO_DOT);

  return (
    <Box width={'100%'} padding={rem(2)}>
      <InputGroup
        id={createDomName(id, 'controls')}
        className={createDomName(className, 'controls')}
        as={'form'}
        display={'flex'}
        width={'100%'}
        borderRadius={'xs'}
        transition={'background-color 170ms ease-in-out'}
        onSubmit={onSubmitMessage}
        onKeyUp={stopEventPropagation}
        onKeyDown={stopEventPropagation}
        onKeyPress={stopEventPropagation}
      >
        {browserConfig.BROWSER?.isMobile ? null : (
          <InputLeftElement>
            <EmojiButton
              id={createDomName(id, 'emoji-button')}
              className={createDomName(className, 'emoji-button')}
              inputRef={inputRef}
              isSubmitted={submitTimeout}
              placement={'top-start'}
              isDisabled={!canInput}
              onEmojiSelect={onEmojiSelect}
            />
          </InputLeftElement>
        )}

        <Input
          id={createDomName(id, 'input')}
          className={createDomName(className, 'input')}
          ref={inputRef}
          maxLength={interactionToolsConfig.CHAT.MAX_MESSAGE_LENGTH}
          placeholder={placeholder}
          value={message}
          autoComplete={'off'}
          isDisabled={!canInput}
          fontFamily={EMOJI_FONT_FAMILY}
          fontSize={rem(browserConfig.BROWSER?.isMobile ? 16 : 14)}
          lineHeight={rem(22)}
          _disabled={{
            cursor: 'not-allowed',
            backgroundColor: 'background-blur',
          }}
          onChange={onMessageEdited}
        />

        <InputRightElement>
          <IconButton
            id={createDomName(id, 'send-button')}
            className={createDomName(className, 'send-button')}
            aria-label={'chat submit button'}
            data-chat-submit
            type={'submit'}
            size={'sm'}
            variant={'minimal'}
            icon={<Send />}
            isDisabled={!canSubmit}
            transition={'none'}
            _hover={{
              backgroundColor: 'transparent!important',
            }}
          />
        </InputRightElement>
      </InputGroup>
    </Box>
  );
}
